import GridView from "components/GridView";
import { GridSchema, GridViewHeaderProps } from "components/GridView/GridView.types";
import { BreadcrumbsParamsType, useBreadcrumb } from "contexts/breadcrumb/BreadcrumbContext";
import useGridData from "hooks/useGridData";
import useSystemDateTheme from "hooks/useSystemDateTheme";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {CourseOrderLocalResult, CourseOrderStatus} from "services/courseOrder/models/CourseOrderType";
import {useCookieContext} from "../../../contexts";
import { useTitle } from "hooks/useTitle";

type OrdersGridViewProps = {
  courseId?: string | number;
  userId?: string;
  columnsToHide?: (
    | "id"
    | "courseName"
    | "applicantName"
    | "publicationType"
    | "applicationDate"
    | "centreManagerName"
    | "courseOrderPhase"
    | "responsibleEntity"
    | "courseOrderStatus"
    | "courseSerialNumber" 
      |"orderType"
    | "actions"
  )[];
  breadcrumbs?: BreadcrumbsParamsType[];
  centreManagerId?: string;
};

const defaultColumnsToHide: OrdersGridViewProps["columnsToHide"] = ["actions", "courseSerialNumber"];
const defaultBreadcrumbs: BreadcrumbsParamsType[] = [
  {
    localizationKey: "allOrders",
    path: "/home/orders",
    menuItemId: "allOrders",
  },
];

const OrdersGridView: React.FC<OrdersGridViewProps> = ({ courseId, userId, columnsToHide, breadcrumbs ,centreManagerId}) => {
  const {isAr} = useCookieContext();
  breadcrumbs = breadcrumbs ? breadcrumbs : defaultBreadcrumbs;
  columnsToHide = columnsToHide ? columnsToHide : defaultColumnsToHide;

  const { t } = useTranslation("Orders");
  useTitle(t("allOrders"));
  const navigate = useNavigate();
  const { setBreadcrumbs } = useBreadcrumb();
  const { toSystemThemeDateFormat } = useSystemDateTheme();
console.log("centreManagerId", centreManagerId);
  const { loading, gridData, totalCount, exportToExcel } = useGridData<"courseOrderService", CourseOrderLocalResult>({
    service: "courseOrderService",
    endpoint: "getAllCourseOrders",
    exportExcelEndpoint: "exportCourseOrdersToExcel",
    exportFileName: "courseOrder",
    localizationNameSpace: "CourseOrders",
    defaultFilters: [
      ...(courseId ? [{ field: "CourseId", value: courseId, operator: "equal" }] : []),
      ...(userId ? [{ field: "CreatedBy", value: userId, operator: "equal" }] : []),
      ...(centreManagerId ? [{ field: "CentreManagement.CentreManagerId", value: centreManagerId, operator: "equal" }] : []),
    ],
  });



  useEffect(() => {
    setBreadcrumbs(breadcrumbs ?? []);
  }, []);
  
  const gridSchema = useMemo<GridSchema[]>(
    () => [
      {
        field: "SerialNumber",
        displayName: t("orderNumber"),
        sortable: true,
        filterable: true,
        showOnMobile: true,
        isHidden: columnsToHide?.includes("id"),
        render: (row: CourseOrderLocalResult) => {
          return (
            <button className="btn btn-link" onClick={() => navigate(`/home/orders/${row.id}`)}>
              {row.serialNumber}
            </button>
          );
        },
      },
      {
        field: "CourseDetail.DisplayName",
        displayName: t("courseName"),
        sortable: true,
        filterable: true,
        isHidden: columnsToHide?.includes("courseName"),
        render: (row: CourseOrderLocalResult) => {
          return <div>{row.courseDetail.displayName}</div>;
        },
      },
      {
        accessor: "appliedUserName",
        field: "AppliedUserId",
        displayName: t("applicantName"),
        sortable: true,
        filterable: true,
        filterOptions: {
          type: "serverSideSelect",
          serverSideSelectProps: {
            label: t("applicantName"),
            service: "accountService",
            endpoint: "getUsersDropdown",
            idValueKey: "id",
            isPaginated: true,
            textValueKey: isAr ? "arabicName" : "englishName",
            useReactSelect: true,
          }
        },
         isHidden: columnsToHide?.includes("applicantName"),
        render: (row: CourseOrderLocalResult) => {
          return <div>{isAr?row.appliedUser.arabicName: row.appliedUser.englishName}</div>;
        },
      },
      {
        field: "courseSerialNumber",
        displayName: t("courseNumber"),
        isHidden: columnsToHide?.includes("courseSerialNumber"),
      },
      {
        field: "publicationType",
        displayName: t("publicationTypeCategory"),
        isHidden: columnsToHide?.includes("publicationType"),
        render(row: CourseOrderLocalResult) {
          return `${row.typeOfScientific?.typeOfScientificDetail.displayName ?? ""} - ${
            row.scientificClassification?.scientificClassificationDetail?.displayName ?? ""
          }`;
        },
      },
      {
        accessor:"applicationDate",
        field: "CreatedOn",
        displayName: t("applicationDate"),
        sortable: true,
        filterable: true,
        filterOptions: {
          type: "date",
        },
        isHidden: columnsToHide?.includes("applicationDate"),
        render: (row: CourseOrderLocalResult) => {
          return toSystemThemeDateFormat(row.createdOn);
        },
      },
      {
        field: "orderType",
        displayName: t("orderType"),
        showOnMobile: true,
        isHidden: columnsToHide?.includes("orderType"),
        render: (row: CourseOrderLocalResult) => {
          return row.orderType
        }
      },
      {
        field: "courseOrderPhase",
        displayName: t("orderPhase"),
        showOnMobile: true,
        isHidden: columnsToHide?.includes("courseOrderPhase")
      },
      {
        accessor:"courseOrderStatus",
        field: "CourseOrderStatusId",
        displayName: t("orderStatus"),
        showOnMobile: true,
        sortable: true,
        filterable: true,
        filterOptions: {
          type: "select",
          options: Object.values(CourseOrderStatus).map((status) => ({
            id: status.id,
            value: t(status.label)
          })),
        },
        isHidden: columnsToHide?.includes("courseOrderStatus"),
        render: (row: CourseOrderLocalResult) => {
          const statusClassName =
            row.courseOrderStatus === "Completed" ? "bg-success text-success " : "bg-primary text-primary";
          return (
            <span className={`badge bg-opacity-10  rounded-4 ${statusClassName} py-2`}>{t(row.courseOrderStatus)}</span>
          );
        },
      },
    ],
    [t, toSystemThemeDateFormat, columnsToHide],
  );

  const viewHeader: GridViewHeaderProps = {
    title: t("orders"),
    singularName: t("order"),
    totalCount: totalCount,
    onExportClick: exportToExcel,
  };

  return (
    <GridView
      loading={loading}
      gridProps={{
        data: gridData ?? [],
        gridSchema,
      }}
      viewHeaderProps={viewHeader}
    />
  );
};

export default OrdersGridView;
